/* @flow */

import type { CheckOutSuccessResponse } from '../chargebee/types';
import type { KeyValuePair } from '../jsHelpers/types';

import ChargebeeWrapper from '../chargebee/chargebee';
import LoadChecker from '../chargebee/loadChecker';
import { logError, logInfo } from '../debug/debug';
import SentryWrapper from '../debug/sentry';
import { SentryTagName, SentryTagValue } from '../debug/sentryTypes';
import Localizer from '../localization';
import { getPriceAsInteger } from '../maths/maths';
import { Messenger, MessengerEvents } from '../messenger';

// After this time, page load is considered a failure (in ms)
const EXTERNAL_PAYMENT_SYSTEM_LOAD_TIMEOUT = 20_000;

const initialize: (token: string, urls: KeyValuePair<string>) => void = (token, urls) => {
  if (!process.env.REACT_APP_CHARGEBEE_INSTANCE) {
    // Chargebee disabled
    return;
  }

  try {
    ChargebeeWrapper.initialize(token, urls);
  } catch (error) {
    Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize('common.messages.errors.payment_system_not_loaded'));
    logError(error.message);
    SentryWrapper.error({
      breadcrumbs: ['ChargebeeWrapper.initialize error'],
      message: error.message,
      tagName: SentryTagName.Component,
      tagValue: SentryTagValue.PaymentSystem,
    });
  }
};

const destroy: () => void = () => {
  if (!ChargebeeWrapper) {
    return;
  }

  ChargebeeWrapper.destroy();
};

const showDebug: () => void = () => {
  if (!ChargebeeWrapper) {
    logInfo('Chargebee not initialized');
    return;
  }

  ChargebeeWrapper.showDebug();
};

const genericErrorCallback: (error: Error | string) => void = (error) => {
  Messenger.emit(MessengerEvents.NOTIFY_ERROR, typeof error === 'string' ? error : error.message);
};

const openSelfCare: () => void = () => {
  if (!ChargebeeWrapper) {
    logError('Chargebee not initialized');
    return;
  }

  const timer = new LoadChecker(EXTERNAL_PAYMENT_SYSTEM_LOAD_TIMEOUT, genericErrorCallback, new Error(Localizer.localize('external_payment_system.portal.load_failure')));

  if (!ChargebeeWrapper) {
    timer.clear();
    return;
  }

  ChargebeeWrapper.openPortal(timer).catch((error) => {
    if (timer.timedOut) {
      // Ignore error since time out message has already been displayed
      return;
    }

    timer.clear();
    genericErrorCallback(error);
  });
};

const openCheckOut: (vtiId: number, title: string, originalPrice: string, promoCode: string | null, discountPrice: string | null) => Promise<CheckOutSuccessResponse | void> = (
  vtiId,
  title,
  originalPrice,
  promoCode,
  discountPrice,
) => {
  if (!ChargebeeWrapper) {
    return Promise.reject(new Error('Chargebee not initialized'));
  }

  let description = title;
  if (promoCode) {
    description = `${description} (prix original : ${originalPrice})`;
  }

  const discountPriceAsInteger = discountPrice !== null ? getPriceAsInteger(discountPrice) : null;
  const timer = new LoadChecker(EXTERNAL_PAYMENT_SYSTEM_LOAD_TIMEOUT, genericErrorCallback, new Error(Localizer.localize('external_payment_system.check_out.load_failure')));

  return new Promise((resolve, reject) => {
    ChargebeeWrapper?.openCheckOut(vtiId, description, getPriceAsInteger(originalPrice) ?? 0, promoCode, discountPriceAsInteger, timer)
      .then(resolve)
      .catch((error) => {
        if (timer.timedOut) {
          // Ignore error since time out message has already been displayed
          return;
        }

        timer.clear();
        reject(error);
      });
  });
};

export { destroy, initialize, openSelfCare, openCheckOut, showDebug };
